import { useEffect, useId, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, Filter as FilterIcon } from 'lucide-react'
import { clsx } from 'clsx'

function readStored(persistKey, defaultOpen) {
  try {
    const stored = localStorage.getItem(persistKey)
    if (stored === '1') return true
    if (stored === '0') return false
  } catch {}
  if (typeof defaultOpen === 'boolean') return defaultOpen
  if (typeof window !== 'undefined' && typeof window.matchMedia === 'function') {
    return window.matchMedia('(min-width: 1280px)').matches
  }
  return true
}

/**
 * Collapsible filter bar with localStorage-persisted open state.
 *
 * Header row (always visible): "Filters" disclosure button + chevron, and the
 * active-filter summary when collapsed. Body slides in/out below.
 *
 * Default open state is read from localStorage if present; otherwise from
 * `defaultOpen`; otherwise from a `(min-width: 1280px)` media query (open at
 * xl and above, closed below).
 *
 * persistKey: string  — e.g. "aeo:filters:open:allocation"
 * activeSummary?: ReactNode
 * defaultOpen?: boolean
 * children: ReactNode  — the filter controls
 * className?: string
 */
export default function CollapsibleFilterBar({
  persistKey,
  activeSummary,
  defaultOpen,
  children,
  className = '',
}) {
  const [open, setOpen] = useState(() => readStored(persistKey, defaultOpen))

  useEffect(() => {
    try { localStorage.setItem(persistKey, open ? '1' : '0') } catch {}
  }, [persistKey, open])

  const panelId = useId()

  return (
    <div className={clsx('glass-card mb-4 overflow-hidden', className)}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-white/[0.03] transition-colors"
      >
        <FilterIcon size={13} className="text-slate-500 flex-shrink-0" />
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
          Filters
        </span>
        {!open && activeSummary && (
          <span className="flex-1 min-w-0 text-xs text-slate-400 truncate">
            {activeSummary}
          </span>
        )}
        <ChevronDown
          size={14}
          className={clsx(
            'ml-auto text-slate-500 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            id={panelId}
            key="panel"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-white/5">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
